import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useData } from "../context/DataContext";

export default function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { posts, API } = useData();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const found = posts.find((p) => p.id === id);
    if (found) {
      setPost(found);
      setLoading(false);
    } else if (posts.length > 0) {
      // Post not found
      setLoading(false);
    }
  }, [id, posts]);

  useEffect(() => {
    if (post) {
      document.title = `${post.title} | Journal enramá`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute(
          "content",
          post.excerpt || "Descubre más detalles sobre esta historia en el journal oficial de enramá."
        );
      }
    }
  }, [post]);

  useEffect(() => {
    if (!post || !post.content) return;

    const intervals = [];

    const timer = setTimeout(() => {
      const carousels = document.querySelectorAll(".journal-carousel");
      carousels.forEach((carousel) => {
        const track = carousel.querySelector(".journal-carousel-track");
        const prevBtn = carousel.querySelector(".prev-btn");
        const nextBtn = carousel.querySelector(".next-btn");
        const dotsContainer = carousel.querySelector(".journal-carousel-dots");
        const slides = carousel.querySelectorAll(".journal-carousel-slide");

        if (!track || slides.length === 0) return;

        // Clear existing dots
        if (dotsContainer) {
          dotsContainer.innerHTML = "";
        }

        // Create dots only if there's more than 1 slide
        const dotElements = [];
        if (slides.length > 1 && dotsContainer) {
          slides.forEach((_, idx) => {
            const dot = document.createElement("button");
            dot.type = "button";
            dot.className = `journal-carousel-dot${idx === 0 ? " active" : ""}`;
            dot.setAttribute("aria-label", `Diapositiva ${idx + 1}`);
            dot.style.border = "none"; // Reset default button border
            dot.addEventListener("click", () => {
              track.scrollTo({
                left: idx * track.clientWidth,
                behavior: "smooth",
              });
            });
            dotsContainer.appendChild(dot);
            dotElements.push(dot);
          });
        }

        const updateDots = () => {
          if (dotElements.length === 0) return;
          const index = Math.round(track.scrollLeft / track.clientWidth);
          dotElements.forEach((dot, idx) => {
            if (idx === index) {
              dot.classList.add("active");
            } else {
              dot.classList.remove("active");
            }
          });
        };

        // Scroll listener for dots synchronization
        track.addEventListener("scroll", updateDots);

        // Previous button action
        if (prevBtn) {
          prevBtn.onclick = () => {
            track.scrollBy({ left: -track.clientWidth, behavior: "smooth" });
          };
        }

        // Next button action
        if (nextBtn) {
          nextBtn.onclick = () => {
            track.scrollBy({ left: track.clientWidth, behavior: "smooth" });
          };
        }

        // Hide controls if only 1 slide
        if (slides.length <= 1) {
          if (prevBtn) prevBtn.style.display = "none";
          if (nextBtn) nextBtn.style.display = "none";
          if (dotsContainer) dotsContainer.style.display = "none";
        } else {
          // Initialize Autoplay for multiple slides
          let intervalId = null;

          const startAutoplay = () => {
            if (!intervalId) {
              intervalId = setInterval(() => {
                const currentIndex = Math.round(track.scrollLeft / track.clientWidth);
                const nextIndex = (currentIndex + 1) % slides.length;
                track.scrollTo({
                  left: nextIndex * track.clientWidth,
                  behavior: "smooth",
                });
              }, 4000);
              intervals.push(intervalId);
            }
          };

          const stopAutoplay = () => {
            if (intervalId) {
              clearInterval(intervalId);
              const idx = intervals.indexOf(intervalId);
              if (idx > -1) intervals.splice(idx, 1);
              intervalId = null;
            }
          };

          startAutoplay();

          // Add hover and touch event listeners to pause autoplay
          carousel.addEventListener("mouseenter", stopAutoplay);
          carousel.addEventListener("mouseleave", startAutoplay);
          carousel.addEventListener("touchstart", stopAutoplay);
          carousel.addEventListener("touchend", startAutoplay);
        }
      });
    }, 150);

    return () => {
      clearTimeout(timer);
      intervals.forEach(clearInterval);
    };
  }, [post]);

  const resolveImg = (src) => {
    if (!src) return null;
    if (src.startsWith("http")) return src;
    return `${API}${src}`;
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString("es-DO", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <div className="loading-spinner" style={{ minHeight: "60vh" }}>
        <div className="spinner" />
        <span>Cargando…</span>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="empty-state" style={{ minHeight: "60vh" }}>
        <h3>Publicación no encontrada</h3>
        <p>La publicación que buscas no existe o fue eliminada.</p>
        <button
          className="about-btn"
          style={{ marginTop: 20 }}
          onClick={() => navigate("/journal")}
        >
          ← Volver al Journal
        </button>
      </div>
    );
  }

  return (
    <article className="blog-post-page">
      <button className="back-btn" onClick={() => navigate("/journal")}>
        ← Volver al Journal
      </button>

      {post.coverImage && (
        <img
          src={resolveImg(post.coverImage)}
          alt={post.title}
          className="blog-post-cover"
          onError={(e) => (e.target.style.display = "none")}
        />
      )}

      <h1>{post.title}</h1>

      <div className="blog-post-meta">
        <span>📅 {formatDate(post.date)}</span>
        {post.author && <span>✍ {post.author}</span>}
        {post.tags?.length > 0 && (
          <div className="blog-tags">
            {post.tags.map((t) => (
              <span key={t} className="blog-tag">{t}</span>
            ))}
          </div>
        )}
      </div>

      <div 
        className="blog-post-content" 
        dangerouslySetInnerHTML={{ 
          __html: post.content ? post.content.replaceAll('/uploads/', `${API}/uploads/`) : "" 
        }}
      />
    </article>
  );
}
